import React, { useState } from 'react';

import { emailTemplates } from '../../utils/stubData';

function EmailTemplates() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">Email Templates</h2>
          <p className="text-hdlcc-textMuted">Manage automated email templates</p>
        </div>
        <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
          Create New Template
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Templates</h3>
            <div className="space-y-2">
              {emailTemplates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template);
                    setIsEditing(false);
                  }}
                  className={`w-full text-left p-3 rounded transition-colors ${
                    selectedTemplate?.id === template.id
                      ? 'bg-hdlcc-accentRed text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-hdlcc-textDark border border-gray-200'
                  }`}
                >
                  <div className="font-semibold">{template.name}</div>
                  <div
                    className={`text-xs mt-1 ${selectedTemplate?.id === template.id ? 'text-white' : 'text-hdlcc-textMuted'}`}
                  >
                    {template.subject}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Template Preview/Edit */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <div className="bg-hdlcc-bgWhite p-6 rounded shadow-card border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-hdlcc-textDark">{selectedTemplate.name}</h3>
                <div className="flex space-x-2">
                  {!isEditing ? (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                      >
                        Edit
                      </button>
                      <button className="bg-gray-100 text-hdlcc-textDark px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition-colors">
                        Send Test Email
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="bg-gray-100 text-hdlcc-textDark px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="template-name"
                      className="block text-sm text-hdlcc-textMuted mb-2"
                    >
                      Template Name
                    </label>
                    <input
                      id="template-name"
                      type="text"
                      defaultValue={selectedTemplate.name}
                      className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="template-subject"
                      className="block text-sm text-hdlcc-textMuted mb-2"
                    >
                      Subject Line
                    </label>
                    <input
                      id="template-subject"
                      type="text"
                      defaultValue={selectedTemplate.subject}
                      className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="template-body"
                      className="block text-sm text-hdlcc-textMuted mb-2"
                    >
                      Email Body
                    </label>
                    <textarea
                      id="template-body"
                      defaultValue={selectedTemplate.body.replace(/<[^>]*>/g, '')}
                      rows={12}
                      className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none font-mono text-sm"
                    />
                  </div>

                  <div>
                    <div className="block text-sm text-hdlcc-textMuted mb-2">
                      Available Variables
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map(variable => (
                        <span
                          key={variable}
                          className="px-3 py-1 bg-gray-50 rounded text-sm font-mono cursor-pointer hover:bg-gray-100 text-hdlcc-textDark border border-gray-200"
                          title="Click to copy"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="block text-sm text-hdlcc-textMuted mb-2">Subject</div>
                    <div className="p-3 bg-gray-50 rounded text-hdlcc-textDark border border-gray-200">
                      {selectedTemplate.subject}
                    </div>
                  </div>

                  <div>
                    <div className="block text-sm text-hdlcc-textMuted mb-2">Preview</div>
                    <div
                      className="p-4 bg-white text-black rounded border border-gray-200"
                      dangerouslySetInnerHTML={{ __html: selectedTemplate.body }}
                    />
                  </div>

                  <div>
                    <div className="block text-sm text-hdlcc-textMuted mb-2">Variables Used</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map(variable => (
                        <span
                          key={variable}
                          className="px-3 py-1 bg-gray-50 rounded text-sm font-mono text-hdlcc-textDark border border-gray-200"
                        >
                          {`{{${variable}}}`}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="block text-sm text-hdlcc-textMuted mb-2">Last Updated</div>
                    <div className="text-sm text-hdlcc-textDark">
                      {new Date(selectedTemplate.updatedAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-hdlcc-bgWhite p-12 rounded shadow-card border border-gray-200 text-center">
              <p className="text-hdlcc-textMuted">Select a template to view or edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailTemplates;
